/**
 * League selector rules shared by the header and AppRouter: which leagues the
 * header lists, which one a fresh session lands on, and the short label shown
 * in the compact header trigger (the full name stays in the options and in the
 * trigger's `title`).
 */

export interface SelectableLeague {
  id: string;
  name?: string | null;
  name_ar?: string | null;
  code?: string | null;
  is_active?: boolean | null;
}

const isActive = (l: SelectableLeague) => l.is_active !== false;

/** Full league name in the active UI language, falling back to the other one. */
export function getLeagueFullLabel(l: SelectableLeague, isArabicUi: boolean): string {
  const name = (l.name ?? '').trim();
  const nameAr = (l.name_ar ?? '').trim();
  return (isArabicUi ? nameAr || name : name || nameAr) || (l.code ?? '');
}

const DASH = '\\s*[-–—]\\s*';
const REGIONAL_RE = new RegExp(`^Ligue\\s+R[ée]gionale\\s+de\\s+Football${DASH}(.+)$`, 'i');
const WILAYA_RE = new RegExp(`^Ligue\\s+(?:de\\s+)?Wilaya\\s+de\\s+Football${DASH}(.+)$`, 'i');
const SHORT_CODE_RE = /^[A-Z]{2,6}$/;

/**
 * Compact label for the header trigger.
 * « Ligue Régionale de Football - Annaba » → « LRF Annaba »,
 * « Ligue Wilaya de Football - Tébessa » → « LWF Tébessa »,
 * other leagues → their code when it is a short acronym (LIRF, LFP, LNFA),
 * else the full name. In Arabic the full `name_ar` is kept when present (no
 * Arabic acronym is invented); without it the French rule applies.
 */
export function getLeagueShortLabel(l: SelectableLeague, isArabicUi: boolean): string {
  const nameAr = (l.name_ar ?? '').trim();
  if (isArabicUi && nameAr) return nameAr;

  const name = (l.name ?? '').trim();
  const regional = name.match(REGIONAL_RE);
  if (regional) return `LRF ${regional[1].trim()}`;
  const wilaya = name.match(WILAYA_RE);
  if (wilaya) return `LWF ${wilaya[1].trim()}`;

  const code = (l.code ?? '').trim();
  if (SHORT_CODE_RE.test(code)) return code;
  return getLeagueFullLabel(l, isArabicUi);
}

/**
 * Leagues a session may land on or switch to: the active ones among the
 * accessible leagues. When the user can access no active league at all, every
 * accessible league stays usable so the app never ends up without a league.
 */
export function getUsableLeagues<T extends SelectableLeague>(accessible: T[]): T[] {
  const active = accessible.filter(isActive);
  return active.length > 0 ? active : accessible;
}

/**
 * Header options: the usable leagues, plus the current one when it is
 * accessible but inactive, so the trigger never shows an unknown value. That
 * extra entry only covers a transient render: ensureLeague replaces an
 * inactive current league as soon as an active one is accessible (when none
 * is, every accessible league is already usable). Order follows `accessible`.
 */
export function getHeaderLeagueOptions<T extends SelectableLeague>(
  accessible: T[],
  currentLeagueId: string | null | undefined,
): T[] {
  const usable = new Set(getUsableLeagues(accessible).map((l) => l.id));
  return accessible.filter((l) => usable.has(l.id) || (!!currentLeagueId && l.id === currentLeagueId));
}

/**
 * League a session should be on. Keeps the current league when it is usable;
 * otherwise the first usable league that has groups in the current season
 * (`leagueIdsWithGroups`), else the first usable league. Never an inactive
 * league while an active one is accessible (LFP was the old default).
 */
export function pickDefaultLeagueId(
  accessible: SelectableLeague[],
  currentLeagueId: string | null | undefined,
  leagueIdsWithGroups: ReadonlySet<string> | null | undefined,
): string {
  const usable = getUsableLeagues(accessible);
  if (currentLeagueId && usable.some((l) => l.id === currentLeagueId)) return currentLeagueId;
  const withGroups = leagueIdsWithGroups
    ? usable.find((l) => leagueIdsWithGroups.has(l.id))
    : undefined;
  return (withGroups ?? usable[0])?.id ?? '';
}
